import { Dropdown } from '@hg/stylizator/components'
import { tablet } from '@hg/stylizator/utils'
import * as React from 'react'
import styled, { useTheme } from 'styled-components'

import { useTranslation } from 'app/hooks/use-translation'
import CopyLink from 'app/icons/CopyLink'
import FacebookLogoCircle from 'app/icons/FacebookLogoCircle'
import { default as ShareIcon } from 'app/icons/Share'
import TwitterLogoCircle from 'app/icons/TwitterLogoCircle'
import { FACEBOOK_APP_ID, FACEBOOK_WEB_INTENT_SHARE, TWITTER_WEB_INTENT_SHARE } from 'app/parameters'
import { TPageData } from 'app/service/api/theme'

type TProps = Pick<TPageData<'post'>, 'title' | 'permalink'>

const ShareButton: React.FC<TProps> = ({ title, permalink }) => {
  const theme = useTheme()
  const t = useTranslation()

  const handleShare = async () => {
    const shareData: ShareData = {
      title,
      url: permalink,
    }

    try {
      await window?.navigator?.share(shareData)
    } catch (e) {
      console.error('Failed to share', e)
    }
  }

  const handleShareTwitter = () => {
    const params = new URLSearchParams({
      url: permalink,
    })

    window.open(`${TWITTER_WEB_INTENT_SHARE}?${params.toString()}`, '_blank')
  }

  const handleShareFacebook = () => {
    const params = new URLSearchParams({
      app_id: FACEBOOK_APP_ID,
      display: 'popup',
      href: permalink,
      redirect_uri: permalink,
    })

    window?.open(`${FACEBOOK_WEB_INTENT_SHARE}?${params.toString()}`, '_blank')
  }

  const handleCopyLink = async () => {
    try {
      await window?.navigator?.clipboard.writeText(permalink)
    } catch (e) {
      console.error('Failed to copy link', e)
    }
  }

  const canShare = window?.navigator !== undefined && 'share' in navigator

  return canShare ? (
    <InlineButton onClick={handleShare}>
      <ShareIcon color={theme.color.blue[60]} />
      <Text>{t('share')}</Text>
    </InlineButton>
  ) : (
    <Dropdown
      icon={<ShareIcon color={theme.color.blue[60]} />}
      label={<Text>{t('share')}</Text>}
      variants={{ mode: 'dropdown', size: 'regular', type: 'left' }}
      items={[
        {
          icon: <FacebookLogoCircle width={18} height={18} color={theme.color.gray[80]} />,
          text: 'Facebook',
          onClick: handleShareFacebook,
        },
        {
          icon: <TwitterLogoCircle width={18} height={18} color={theme.color.gray[80]} />,
          text: 'Twitter',
          onClick: handleShareTwitter,
        },
        {
          icon: <CopyLink width={18} height={18} color={theme.color.gray[80]} />,
          text: t('copy_link'),
          onClick: handleCopyLink,
        },
      ]}
    />
  )
}

const InlineButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  margin: 0;
  padding: 0;
  width: auto;
  overflow: visible;
  background: transparent;
  cursor: pointer;
`

const Text = styled.span`
  display: none;
  color: ${(props) => props.theme.color.blue[60]} !important;

  @media ${tablet} {
    display: block;
  }
`

export default ShareButton
